/* eslint-disable no-prototype-builtins */
import { Utils } from '@genial/utils';

import { FilterModalTypes } from '../../components/FilterModal';
import { DateRangeTypes } from '../../components/FilterModal/filters/DateRange';
import { MultiSliderTypes } from '../../components/FilterModal/filters/MultiSlider';
import { RangeTypes } from '../../components/FilterModal/filters/Range';
import { SectionTypes } from '../../components/FilterModal/filters/Section';

import { fieldNameMapping } from './constants';

interface TransformFiltersOptions {
  transformDateFilter?: boolean;
  transformRangeFieldNames?: boolean;
}

const defaultTransformFiltersOptions: TransformFiltersOptions = {
  transformDateFilter: true,
  transformRangeFieldNames: false,
};

const validateIsRangeFilter = (
  filter: FilterModalTypes.SelectedFiltersProps['filter']
): filter is RangeTypes.RangeProps | MultiSliderTypes.RangeProps => {
  return (
    !!filter &&
    typeof filter === 'object' &&
    filter.hasOwnProperty('min') &&
    filter.hasOwnProperty('max')
  );
};

const validateIsDateFilter = (
  filter: FilterModalTypes.SelectedFiltersProps['filter']
): filter is Date => {
  return !!filter && typeof filter === 'object' && filter instanceof Date;
};

const validateIsDateRangeFilter = (
  filter: FilterModalTypes.SelectedFiltersProps['filter']
): filter is { initialDate: Date; finalDate: Date } => {
  return (
    !!filter &&
    typeof filter === 'object' &&
    'initialDate' in filter &&
    'finalDate' in filter &&
    (filter as DateRangeTypes.SelectionProps).initialDate instanceof Date &&
    (filter as DateRangeTypes.SelectionProps).finalDate instanceof Date
  );
};

const validateIsSelectOrBooleanFilter = (
  filter: FilterModalTypes.SelectedFiltersProps['filter']
): filter is SectionTypes.SelectionProps => {
  return !!filter && Array.isArray(filter) && filter.length > 0;
};

const getCompleteFieldName = (
  fieldName: string,
  options: TransformFiltersOptions,
  extreme: 'Minimum' | 'Maximum'
) => {
  const mainFieldName = `${fieldName}${extreme}`;

  if (!options.transformRangeFieldNames) {
    return mainFieldName;
  }

  return fieldNameMapping[mainFieldName] || mainFieldName;
};

const getMinimumFieldName = (fieldName: string, options: TransformFiltersOptions) => {
  return getCompleteFieldName(fieldName, options, 'Minimum');
};

const getMaximumFieldName = (fieldName: string, options: TransformFiltersOptions) => {
  return getCompleteFieldName(fieldName, options, 'Maximum');
};

export interface FiltersToApplyProps {
  [key: string]: number | string | string[];
}

export const useFilterTransformer = (baseFilters: FilterModalTypes.FilterType[]) => {
  const getCurrentRangeFilter = (fieldName: string) => {
    return baseFilters.find((filter) => filter.fieldName === fieldName);
  };

  const transformFilters = (
    filters: FilterModalTypes.SelectedFiltersProps[],
    options: TransformFiltersOptions = defaultTransformFiltersOptions
  ): FiltersToApplyProps => {
    const info = filters.reduce((acc, { fieldName, filter }) => {
      if (validateIsRangeFilter(filter)) {
        const currentFilter = getCurrentRangeFilter(fieldName) as RangeTypes.Props | undefined;
        const hideMin = currentFilter?.min === filter.min || isNaN(filter.min);
        const hideMax = currentFilter?.max === filter.max || isNaN(filter.max);
        const minimumFieldName = getMinimumFieldName(fieldName, options);
        const maximumFieldName = getMaximumFieldName(fieldName, options);
        const props = {
          ...(!hideMin && { [minimumFieldName]: filter.min }),
          ...(!hideMax && { [maximumFieldName]: filter.max }),
        };
        return { ...acc, ...props };
      }
      if (validateIsDateFilter(filter)) {
        const dateFilter = options.transformDateFilter
          ? Utils.Mask.DateFormat(filter, 'yyyy-MM-dd')
          : filter;
        const props = {
          [fieldName]: dateFilter,
        };
        return { ...acc, ...props };
      }
      if (validateIsDateRangeFilter(filter)) {
        const initialDateFilter = options.transformDateFilter
          ? Utils.Mask.DateFormat(filter.initialDate, 'yyyy-MM-dd')
          : filter.initialDate;
        const finalDateFilter = options.transformDateFilter
          ? Utils.Mask.DateFormat(filter.finalDate, 'yyyy-MM-dd')
          : filter.finalDate;
        const minimumFieldName = getMinimumFieldName(fieldName, options);
        const maximumFieldName = getMaximumFieldName(fieldName, options);
        const props = {
          [minimumFieldName]: initialDateFilter,
          [maximumFieldName]: finalDateFilter,
        };
        return { ...acc, ...props };
      }
      if (validateIsSelectOrBooleanFilter(filter)) {
        const props = {
          [fieldName]: filter,
        };
        return { ...acc, ...props };
      }
      return acc;
    }, {});
    return info;
  };

  return { transformFilters };
};
